import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface Category {
  id: string;
  category_name: string;
  category_description: string;
  createdAt: string;
  updatedAt: string;
}

interface CategoryState {
  categories: Category[];
  loading: boolean;
  creating: boolean;
  deleting: boolean;
  error: string | null;
}

interface CreateCategoryPayload {
  category_name: string;
  category_description: string;
}

/* -------------------------------------------------------------------------- */
/*                               Initial State                                */
/* -------------------------------------------------------------------------- */

const initialState: CategoryState = {
  categories: [],
  loading: false,
  creating: false,
  deleting: false,
  error: null,
};

/* -------------------------------------------------------------------------- */
/*                               Async Thunks                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetch all categories
 * GET: /category/all
 */
export const fetchCategories = createAsyncThunk<Category[]>(
  "category/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpService.get(API_CONFIG.path.category_all);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch categories");
    }
  }
);

/**
 * Create category
 * POST: /category/create
 */
export const createCategory = createAsyncThunk<Category, CreateCategoryPayload>(
  "category/create",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await httpService.post(API_CONFIG.path.category_create, payload);

      // Refresh categories list
      await dispatch(fetchCategories());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create category");
    }
  }
);

/**
 * Delete category
 * DELETE: /category/delete/{id}
 */
export const deleteCategory = createAsyncThunk<void, string>(
  "category/delete",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await httpService.deleteRequest(`${API_CONFIG.path.category_delete}/${id}`);

      // Refresh categories list
      await dispatch(fetchCategories());
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete category");
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                  Slice                                     */
/* -------------------------------------------------------------------------- */

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    clearCategoryError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<Category[]>) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      })

      // Delete Category
      .addCase(deleteCategory.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });
  },
});

/* -------------------------------------------------------------------------- */
/*                                  Exports                                   */
/* -------------------------------------------------------------------------- */

export const { clearCategoryError } = categorySlice.actions;

export default categorySlice.reducer;
